/**
 * Whether a flip actually turned the camera round.
 *
 * The question exists because `facingMode` travels as an `ideal` constraint
 * (see videoConstraints in webrtc.ts, which explains why it has to): a phone
 * that cannot open its second camera while the first one is live does not
 * refuse. It hands back the camera that is already open — resolved promise,
 * a video track, no error anywhere — and from the page that is
 * indistinguishable from a flip that worked. From the person holding it, it is
 * «нажимаю, и ничего», eight times in fifteen seconds, which is exactly what
 * one call's journal showed.
 *
 * Here rather than in webrtc.ts because it is arithmetic over two
 * MediaTrackSettings and nothing else: the one part of a flip that can be
 * tested without a camera, a peer connection or a phone.
 */
import type { Facing } from './capture.ts';

/** As much as a track will say about which camera it came from. */
export interface CameraId {
  /** Chrome answers this on a phone; desktop Chrome and Safari often do not. */
  facing: Facing | null;
  /** Always answered once the page holds a grant, which during a call it does. */
  deviceId: string | null;
  /** «camera2 0, facing back». For the journal, not for the arithmetic. */
  label: string | null;
}

/** What a live track says about itself, with the browser's blanks made null. */
export function cameraOf(track: MediaStreamTrack): CameraId {
  const settings = track.getSettings();
  const facing = settings.facingMode;
  return {
    facing: facing === 'user' || facing === 'environment' ? facing : null,
    deviceId: settings.deviceId ?? null,
    label: track.label === '' ? null : track.label,
  };
}

/**
 * `turned` and `same` are findings; `unknown` is the browser declining to say,
 * which must never be read as either. Treating it as `same` would undo a flip
 * that worked on every desktop browser, and treating it as `turned` would put
 * the failure back where it was.
 */
export type FlipVerdict = 'turned' | 'same' | 'unknown';

export function flipVerdict(before: CameraId | null, after: CameraId, wanted: Facing): FlipVerdict {
  // The direct answer, when there is one: what the new camera says it is.
  if (after.facing !== null) return after.facing === wanted ? 'turned' : 'same';
  // Otherwise the indirect one. Two different devices are two cameras whatever
  // either of them is pointing at, and the same device is the same camera.
  if (before?.deviceId && after.deviceId) {
    return before.deviceId === after.deviceId ? 'same' : 'turned';
  }
  return 'unknown';
}

/** One camera as a journal reads it. */
export function describeCamera(camera: CameraId | null): string {
  if (camera === null) return 'no camera';
  const label = camera.label === null ? null : camera.label.slice(0, 60);
  return [camera.facing ?? 'facing unsaid', label].filter((part) => part !== null).join(' ');
}
